import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { AsyncCombobox } from '@/components/ui/async-combobox';
import { Loader2, X, Shield, Plus, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AiEditor } from '@/ai-editor';

const COLOR_PRESETS = [
  '#6366f1', // indigo
  '#3b82f6', // blue
  '#06b6d4', // cyan
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#ec4899', // pink
  '#8b5cf6', // violet
  '#14b8a6', // teal
  '#f97316', // orange
];

export function RoleTemplateDialog({ isOpen, onClose, template, onSuccess }) {
  const { userProfile, company } = useAuth();
  const isEdit = !!template;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#6366f1');
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [courseSearchValue, setCourseSearchValue] = useState('');
  const [courseDisplayLabel, setCourseDisplayLabel] = useState('');
  const [jobProfile, setJobProfile] = useState(null);
  const [showJobProfile, setShowJobProfile] = useState(false);
  const [saving, setSaving] = useState(false);

  // Populate form for edit mode
  useEffect(() => {
    if (template) {
      setName(template.name || '');
      setDescription(template.description || '');
      setColor(template.color || '#6366f1');
      setSelectedCourses(
        (template.requirements || []).map(r => ({
          course_name: r.course_name || r.name,
          course_link_id: r.course_link_id || null,
          course_link_type: r.course_link_type || null
        }))
      );
      setJobProfile(template.job_profile || null);
      setShowJobProfile(!!template.job_profile);
    } else {
      setName('');
      setDescription('');
      setColor('#6366f1');
      setSelectedCourses([]);
      setJobProfile(null);
      setShowJobProfile(false);
    }
    setCourseSearchValue('');
    setCourseDisplayLabel('');
  }, [template, isOpen]);

  // Course search handler - same pattern as AddQualification
  const handleCourseSearch = useCallback(async (searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') return [];

    try {
      const [apiResults, dbResults] = await Promise.all([
        fetch(
          `https://leqtvmuqmyxkdkaxxfqu.supabase.co/functions/v1/api/courses?search=${encodeURIComponent(searchTerm)}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              apikey: 'bohsa-api-xDZ8kuCTQz3axI8VhhjJOQoW3YMB0jcX'
            }
          }
        )
          .then(res => res.json())
          .then(response => {
            const courses = response?.data || [];
            if (Array.isArray(courses)) {
              return courses.map(course => ({
                value: course.name,
                label: course.name,
                group: 'Verified Courses',
                data: { ...course, title: course.name }
              }));
            }
            return [];
          })
          .catch(() => []),

        supabase
          .from('custom_courses')
          .select('*')
          .ilike('title', `%${searchTerm}%`)
          .or(`company_id.eq.${company?.id},company_id.is.null`)
          .limit(10)
          .then(({ data, error }) => {
            if (error) return [];
            return (data || []).map(course => ({
              value: course.title || course.name,
              label: course.title || course.name,
              group: 'Custom Courses',
              data: course
            }));
          })
      ]);

      const combined = [...apiResults, ...dbResults];
      return combined.reduce((acc, current) => {
        const exists = acc.find(item => item.value === current.value);
        if (!exists) acc.push(current);
        return acc;
      }, []);
    } catch {
      return [];
    }
  }, [company?.id]);

  // Handle course selection from AsyncCombobox
  const handleCourseSelection = useCallback((courseData) => {
    const courseName = courseData?.title || courseData?.name;
    if (!courseName) return;

    // Check if already added
    if (selectedCourses.some(c => c.course_name === courseName)) {
      toast.error('This course is already added');
      return;
    }

    const type = Object.prototype.hasOwnProperty.call(courseData, 'company_id') ? 'custom' : 'verified';

    setSelectedCourses(prev => [
      ...prev,
      {
        course_name: courseName,
        course_link_id: courseData?.id?.toString() || null,
        course_link_type: type
      }
    ]);

    // Reset the combobox
    setCourseSearchValue('');
    setCourseDisplayLabel('');
  }, [selectedCourses]);

  // Remove a course from the list
  const removeCourse = useCallback((courseName) => {
    setSelectedCourses(prev => prev.filter(c => c.course_name !== courseName));
  }, []);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    setSaving(true);
    try {
      let templateId;

      if (isEdit) {
        const { error: updateError } = await supabase
          .from('role_templates')
          .update({
            name: name.trim(),
            description: description.trim() || null,
            color,
            job_profile: jobProfile || null,
            updated_date: new Date().toISOString()
          })
          .eq('id', template.id);

        if (updateError) throw updateError;
        templateId = template.id;

        // Delete old requirements
        const { error: deleteError } = await supabase
          .from('role_template_requirements')
          .delete()
          .eq('role_template_id', template.id);

        if (deleteError) throw deleteError;
      } else {
        const { data: newTemplate, error: createError } = await supabase
          .from('role_templates')
          .insert({
            company_id: userProfile.company_id,
            name: name.trim(),
            description: description.trim() || null,
            color,
            job_profile: jobProfile || null,
            created_by: userProfile.id
          })
          .select()
          .single();

        if (createError) throw createError;
        templateId = newTemplate.id;
      }

      // Insert course requirements (only if there are courses)
      if (selectedCourses.length > 0) {
        const rows = selectedCourses.map(course => ({
          role_template_id: templateId,
          course_name: course.course_name,
          course_link_id: course.course_link_id,
          course_link_type: course.course_link_type
        }));

        const { error: reqError } = await supabase
          .from('role_template_requirements')
          .insert(rows);

        if (reqError) throw reqError;
      }

      toast.success(isEdit ? 'Role template updated' : 'Role template created');
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error saving role template:', err);
      if (err.code === '23505') {
        toast.error('A role template with this name already exists');
      } else {
        toast.error('Failed to save role template');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Role Template' : 'Create Role Template'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update the role template details and courses.'
              : 'Define a reusable role with a set of courses that can be applied to users.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Name & Description */}
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="template-name">Name</Label>
              <Input
                id="template-name"
                placeholder="e.g. Safety Officer"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="template-description">Description (optional)</Label>
              <Input
                id="template-description"
                placeholder="Brief description of the role"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>

          {/* Color */}
          <div className="space-y-1.5">
            <Label>Badge Color</Label>
            <div className="flex items-center gap-1.5 flex-wrap">
              {COLOR_PRESETS.map((c) => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  className={cn(
                    'w-6 h-6 rounded-full border-2 transition-all',
                    color === c ? 'border-gray-900 scale-110' : 'border-transparent hover:scale-105'
                  )}
                  style={{ backgroundColor: c }}
                />
              ))}
              {name.trim() && (
                <Badge
                  variant="outline"
                  className="text-[10px] py-0 px-2 ml-2"
                  style={{
                    backgroundColor: color + '15',
                    borderColor: color + '40',
                    color: color
                  }}
                >
                  {name.trim()}
                </Badge>
              )}
            </div>
          </div>

          {/* Courses Picker */}
          <div className="space-y-1.5">
            <Label>Courses ({selectedCourses.length} added)</Label>
            <AsyncCombobox
              value={courseSearchValue}
              displayLabel={courseDisplayLabel}
              onValueChange={(value) => {
                setCourseSearchValue(value);
                if (!value) setCourseDisplayLabel('');
              }}
              onSelectionChange={handleCourseSelection}
              onSearch={handleCourseSearch}
              groupConfig={{
                'Verified Courses': {
                  icon: Shield,
                  color: '#3b82f6'
                }
              }}
              placeholder="Search for a course to add..."
              emptyText="No courses found. Try a different search."
            />

            {/* Selected courses list */}
            {selectedCourses.length > 0 && (
              <div className="border rounded-md max-h-[160px] overflow-y-auto">
                {selectedCourses.map((course) => (
                  <div
                    key={course.course_name}
                    className="flex items-center gap-2 px-2.5 py-1 border-b last:border-0 group hover:bg-gray-50"
                  >
                    <span className="flex-1 min-w-0 text-sm truncate">{course.course_name}</span>
                    {course.course_link_type && (
                      <span className="text-[10px] text-gray-400 capitalize shrink-0">
                        {course.course_link_type}
                      </span>
                    )}
                    <button
                      onClick={() => removeCourse(course.course_name)}
                      className="shrink-0 p-0.5 rounded hover:bg-red-100 text-gray-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Job Profile */}
          <div className="space-y-1.5">
            {!showJobProfile ? (
              <button
                type="button"
                onClick={() => setShowJobProfile(true)}
                className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
                Add Job Profile
              </button>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <Label>Job Profile (optional)</Label>
                  <button
                    type="button"
                    onClick={() => {
                      setJobProfile(null);
                      setShowJobProfile(false);
                    }}
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="h-3 w-3" />
                    Remove
                  </button>
                </div>
                <div className="border rounded-md overflow-hidden min-h-[160px] max-h-[300px] overflow-y-auto">
                  <AiEditor
                    value={jobProfile || ''}
                    onChange={setJobProfile}
                    placeholder="Describe role duties, expectations, reporting structure..."
                    showVoiceButton={false}
                  />
                </div>
              </>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-1">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isEdit ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default RoleTemplateDialog;
